import React from 'react';
import { Users, X } from 'lucide-react';
import { IconDropdownMenu } from '@/components/ui/IconDropdownMenu';
import { type JobAssignments } from '@/types/jobs';
import { type UserProfile } from '@/types/team';

interface JobAssignmentsFormProps {
    assignments: JobAssignments;
    setAssignments: React.Dispatch<React.SetStateAction<JobAssignments>>;
    orgUsers: UserProfile[];
    availableUsers: UserProfile[];
}

export const JobAssignmentsForm: React.FC<JobAssignmentsFormProps> = ({
    assignments, setAssignments,
    orgUsers, availableUsers
}) => {
    const handleAssignmentChange = (field: keyof JobAssignments, value: string | string[]) => {
        setAssignments(prev => ({ ...prev, [field]: value }));
    };

    const availableTeamMembers = availableUsers.filter(u =>
        u.uid !== assignments.leadTechnicianId &&
        !assignments.teamMemberIds?.includes(u.uid)
    );

    const handleAddTeamMember = (uid: string) => {
        setAssignments(prev => ({
            ...prev,
            teamMemberIds: [...(prev.teamMemberIds || []), uid]
        }));
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-2 text-accent-primary mb-4">
                <Users size={18} />
                <h3 className="text-sm font-black uppercase tracking-widest">Assignments</h3>
            </div>

            {/* 1. SUPERVISOR */}
            <div className="space-y-1">
                <label className="text-micro font-bold text-text-muted uppercase">Supervisor / Dept. Manager</label>
                <select
                    value={assignments.supervisorId || ''}
                    onChange={(e) => handleAssignmentChange('supervisorId', e.target.value)}
                    className="input-field appearance-none"
                >
                    <option value="" className="bg-bg-tertiary">Select Supervisor...</option>
                    {availableUsers.map(u => (
                        <option key={u.uid} value={u.uid} className="bg-bg-tertiary">
                            {u.displayName} {u.role === 'DEPT_MANAGER' ? '(Manager)' : ''}
                        </option>
                    ))}
                </select>
            </div>

            {/* 2. LEAD TECHNICIAN */}
            <div className="space-y-1">
                <label className="text-micro font-bold text-text-muted uppercase">Lead Technician</label>
                <select
                    value={assignments.leadTechnicianId || ''}
                    onChange={(e) => handleAssignmentChange('leadTechnicianId', e.target.value)}
                    className="input-field appearance-none"
                >
                    <option value="" className="bg-bg-tertiary">Select Lead Tech...</option>
                    {availableUsers.map(u => (
                        <option key={u.uid} value={u.uid} className="bg-bg-tertiary">
                            {u.displayName}
                        </option>
                    ))}
                </select>
            </div>

            {/* 3. ADDITIONAL TEAM MEMBERS */}
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <label className="text-micro font-bold text-text-muted uppercase">Additional Team Members</label>
                    <IconDropdownMenu
                        items={availableTeamMembers.map((u) => ({ id: u.uid, label: u.displayName }))}
                        onSelect={handleAddTeamMember}
                        menuTitle="Add Team Member"
                        emptyText="No available team members"
                        direction="up"
                    />
                </div>
                <div className="space-y-2">
                    {/* Selected Members List */}
                    <div className="flex flex-wrap gap-2">
                        {assignments.teamMemberIds?.map(uid => {
                            const user = orgUsers.find(u => u.uid === uid);
                            if (!user) return null;
                            return (
                                <div key={uid} className="flex items-center gap-2 bg-surface-active px-3 py-1.5 rounded-lg border border-divider-subtle">
                                    <span className="text-sm">{user.displayName}</span>
                                    <button
                                        type="button"
                                        onClick={() => setAssignments(prev => ({
                                            ...prev,
                                            teamMemberIds: prev.teamMemberIds?.filter(id => id !== uid)
                                        }))}
                                        className="hover:text-red-400 transition-colors"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
